/**
 * EmptyState — a friendly placeholder for lists / tables with no data.
 *
 * Provides consistent empty-state messaging across the app, replacing
 * ad-hoc Typography + inline strings.
 *
 * @example
 * {items.length === 0 && <EmptyState title="No cases yet" description="Create a case to get started." />}
 * <EmptyState icon={<SearchOff />} title="No results" description={`Nothing matched "${query}"`} />
 */
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { type ReactNode } from 'react';

interface EmptyStateProps {
  /** Headline text */
  title: string;
  /** Optional supporting copy */
  description?: string;
  /** Optional MUI icon rendered above the title */
  icon?: ReactNode;
  /** Optional action button(s) below the description */
  action?: ReactNode;
  /** Extra sx overrides */
  sx?: SxProps<Theme>;
}

export default function EmptyState({
  title,
  description,
  icon,
  action,
  sx,
}: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: { xs: 4, md: 6 },
        px: 3,
        gap: 1.5,
        ...sx,
      }}
    >
      {icon && (
        <Box aria-hidden="true" sx={{ color: 'primary.main', bgcolor: 'action.selected', width: 48, height: 48, display: 'grid', placeItems: 'center', borderRadius: 3, mb: 1, '& .MuiSvgIcon-root': { fontSize: 26 } }}>
          {icon}
        </Box>
      )}
      <Typography variant="h6" color="text.primary">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
}
